import type { Chunk, TimeframeMeta, SymbolMeta, Bar, TimeframeInfo } from "./types";
import { decodeChunk } from "./binary";

const DATA_ROOT = "/data";

// Per-timeframe lazy chunk loader with an LRU cache.
// The global index space for a timeframe = contiguous bar position (session
// gaps are already collapsed at preprocess time). chunkOf(i) = floor(i/chunkSize).
export class TimeframeStore {
  readonly meta: TimeframeMeta;
  private symbol: string;
  private chunks = new Map<number, Chunk>();
  private loading = new Map<number, Promise<Chunk>>();
  private lru: number[] = []; // chunk indices, most-recent last
  private maxResidentChunks: number;

  constructor(symbol: string, meta: TimeframeMeta) {
    this.symbol = symbol;
    this.meta = meta;
    // Keep enough chunks for a wide viewport + prefetch, bounded for the 1s tf.
    // ~1.5M bars resident max => 15 chunks of 100k.
    this.maxResidentChunks = Math.max(6, Math.ceil(1_500_000 / meta.chunkSize));
  }

  get barCount() {
    return this.meta.barCount;
  }
  get chunkSize() {
    return this.meta.chunkSize;
  }

  chunkOf(index: number): number {
    return Math.floor(index / this.meta.chunkSize);
  }

  isChunkLoaded(ci: number): boolean {
    return this.chunks.has(ci);
  }

  getLoadedChunk(ci: number): Chunk | undefined {
    const c = this.chunks.get(ci);
    if (c) this.touch(ci);
    return c;
  }

  private touch(ci: number) {
    const idx = this.lru.indexOf(ci);
    if (idx !== -1) this.lru.splice(idx, 1);
    this.lru.push(ci);
  }

  private evictIfNeeded(keep: Set<number>) {
    while (this.chunks.size > this.maxResidentChunks && this.lru.length) {
      // evict least-recently-used that isn't in the keep set
      let victim = -1;
      for (let k = 0; k < this.lru.length; k++) {
        if (!keep.has(this.lru[k])) {
          victim = this.lru[k];
          break;
        }
      }
      if (victim === -1) break;
      this.lru.splice(this.lru.indexOf(victim), 1);
      this.chunks.delete(victim);
    }
  }

  private async loadChunk(ci: number): Promise<Chunk> {
    const existing = this.chunks.get(ci);
    if (existing) {
      this.touch(ci);
      return existing;
    }
    const inflight = this.loading.get(ci);
    if (inflight) return inflight;

    const url = `${DATA_ROOT}/${this.symbol}/${this.meta.tf}/chunk_${String(ci).padStart(5, "0")}.bin`;
    const p = fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`chunk ${ci} http ${r.status}`);
        return r.arrayBuffer();
      })
      .then((buf) => {
        const chunk = decodeChunk(ci, buf);
        this.chunks.set(ci, chunk);
        this.touch(ci);
        this.loading.delete(ci);
        return chunk;
      })
      .catch((e) => {
        this.loading.delete(ci);
        throw e;
      });
    this.loading.set(ci, p);
    return p;
  }

  // Ensure all chunks covering [lo, hi) are resident (+/- prefetch), then evict.
  // Returns a promise that resolves when the *needed* (non-prefetch) chunks are loaded.
  async ensureRange(lo: number, hi: number, prefetch = 1): Promise<void> {
    lo = Math.max(0, Math.floor(lo));
    hi = Math.min(this.meta.barCount, Math.ceil(hi));
    if (hi <= lo) return;
    const first = this.chunkOf(lo);
    const last = this.chunkOf(hi - 1);
    const need: number[] = [];
    for (let ci = first; ci <= last; ci++) need.push(ci);

    const prefetchList: number[] = [];
    for (let p = 1; p <= prefetch; p++) {
      if (first - p >= 0) prefetchList.push(first - p);
      if (last + p <= this.chunkOf(this.meta.barCount - 1)) prefetchList.push(last + p);
    }

    const keep = new Set<number>([...need, ...prefetchList]);
    // load needed synchronously-awaited, prefetch fire-and-forget
    const needPromises = need.filter((ci) => !this.chunks.has(ci)).map((ci) => this.loadChunk(ci));
    for (const ci of prefetchList) {
      if (!this.chunks.has(ci)) this.loadChunk(ci).catch(() => {});
    }
    this.evictIfNeeded(keep);
    await Promise.all(needPromises);
    this.evictIfNeeded(keep);
  }

  // True if every chunk covering [lo,hi) is already resident (no fetch needed).
  hasRange(lo: number, hi: number): boolean {
    lo = Math.max(0, Math.floor(lo));
    hi = Math.min(this.meta.barCount, Math.ceil(hi));
    if (hi <= lo) return true;
    const first = this.chunkOf(lo);
    const last = this.chunkOf(hi - 1);
    for (let ci = first; ci <= last; ci++) if (!this.chunks.has(ci)) return false;
    return true;
  }

  // O(1) accessors (index must be in a resident chunk; returns NaN otherwise).
  private localOf(index: number): [Chunk | undefined, number] {
    const ci = this.chunkOf(index);
    const chunk = this.chunks.get(ci);
    if (!chunk) return [undefined, 0];
    return [chunk, index - ci * this.meta.chunkSize];
  }

  timeAt(index: number): number {
    const [c, k] = this.localOf(index);
    return c ? c.time[k] : NaN;
  }
  openAt(index: number): number {
    const [c, k] = this.localOf(index);
    return c ? c.open[k] : NaN;
  }
  highAt(index: number): number {
    const [c, k] = this.localOf(index);
    return c ? c.high[k] : NaN;
  }
  lowAt(index: number): number {
    const [c, k] = this.localOf(index);
    return c ? c.low[k] : NaN;
  }
  closeAt(index: number): number {
    const [c, k] = this.localOf(index);
    return c ? c.close[k] : NaN;
  }
  volumeAt(index: number): number {
    const [c, k] = this.localOf(index);
    return c ? c.volume[k] : NaN;
  }
  trAt(index: number): number {
    const [c, k] = this.localOf(index);
    return c ? c.tr[k] : NaN;
  }
  atrAt(index: number): number {
    const [c, k] = this.localOf(index);
    return c ? c.atr[k] : NaN;
  }

  barAt(index: number): Bar | null {
    const [c, k] = this.localOf(index);
    if (!c) return null;
    return {
      index,
      time: c.time[k],
      open: c.open[k],
      high: c.high[k],
      low: c.low[k],
      close: c.close[k],
      volume: c.volume[k],
    };
  }

  // Largest bar index whose open time <= t (or -1 if none). Loads the chunk.
  // This is the anti-lookahead primitive for the replay clock.
  async lastIndexAtOrBefore(t: number): Promise<number> {
    const chunks = this.meta.chunks;
    if (chunks.length === 0) return -1;
    if (t < chunks[0].startTime) return -1;
    const lastChunk = chunks[chunks.length - 1];
    if (t >= lastChunk.endTime) return this.meta.barCount - 1;

    // find chunk whose span contains t (or the one just before a gap)
    let lo = 0,
      hi = chunks.length - 1,
      ci = 0;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const ch = chunks[mid];
      if (t < ch.startTime) hi = mid - 1;
      else if (t > ch.endTime) lo = mid + 1;
      else {
        ci = mid;
        break;
      }
      ci = mid;
    }
    if (t < chunks[ci].startTime) ci = Math.max(0, ci - 1);
    const chunk = await this.loadChunk(ci);
    const base = ci * this.meta.chunkSize;
    const time = chunk.time;
    if (t < time[0]) return base - 1;
    // largest k with time[k] <= t
    let a = 0,
      b = chunk.count - 1,
      res = -1;
    while (a <= b) {
      const mid = (a + b) >> 1;
      if (time[mid] <= t) {
        res = mid;
        a = mid + 1;
      } else b = mid - 1;
    }
    return res >= 0 ? base + res : base - 1;
  }

  // Map a unix-seconds timestamp -> nearest bar index. Loads the containing chunk.
  async timeToIndex(t: number): Promise<number> {
    const chunks = this.meta.chunks;
    if (chunks.length === 0) return 0;
    if (t <= chunks[0].startTime) return 0;
    const lastChunk = chunks[chunks.length - 1];
    if (t >= lastChunk.endTime) return this.meta.barCount - 1;

    // binary search chunk by [startTime, endTime]
    let lo = 0,
      hi = chunks.length - 1,
      found = chunks.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const ch = chunks[mid];
      if (t < ch.startTime) {
        hi = mid - 1;
      } else if (t > ch.endTime) {
        lo = mid + 1;
      } else {
        found = mid;
        break;
      }
      found = mid;
    }
    // clamp found to a sensible chunk (t may fall in a gap between chunks)
    let ci = found;
    if (t < chunks[ci].startTime) ci = Math.max(0, ci - 1);

    const chunk = await this.loadChunk(ci);
    const base = ci * this.meta.chunkSize;
    // binary search within chunk.time
    const time = chunk.time;
    let a = 0,
      b = chunk.count - 1;
    if (t <= time[0]) return base;
    if (t >= time[b]) return base + b;
    while (a <= b) {
      const mid = (a + b) >> 1;
      if (time[mid] < t) a = mid + 1;
      else if (time[mid] > t) b = mid - 1;
      else return base + mid;
    }
    // a is first index with time>t; choose nearer of a-1, a
    const hiIdx = a,
      loIdx = a - 1;
    const nearer = t - time[loIdx] <= time[hiIdx] - t ? loIdx : hiIdx;
    return base + nearer;
  }
}

// Top-level store: discovers symbol timeframes and vends per-tf stores.
export class DataStore {
  readonly symbol: string;
  private symbolMeta!: SymbolMeta;
  private tfStores = new Map<string, TimeframeStore>();
  private tfMetaPromises = new Map<string, Promise<TimeframeStore>>();

  constructor(symbol: string) {
    this.symbol = symbol;
  }

  async init(): Promise<SymbolMeta> {
    const r = await fetch(`${DATA_ROOT}/${this.symbol}/symbol.json`);
    if (!r.ok) throw new Error(`symbol.json http ${r.status}`);
    this.symbolMeta = await r.json();
    return this.symbolMeta;
  }

  get timeframes(): TimeframeInfo[] {
    return this.symbolMeta.timeframes;
  }

  tfInfo(tf: string): TimeframeInfo | undefined {
    return this.symbolMeta.timeframes.find((t) => t.tf === tf);
  }

  async getTimeframe(tf: string): Promise<TimeframeStore> {
    const existing = this.tfStores.get(tf);
    if (existing) return existing;
    const inflight = this.tfMetaPromises.get(tf);
    if (inflight) return inflight;
    const p = fetch(`${DATA_ROOT}/${this.symbol}/${tf}/meta.json`)
      .then((r) => {
        if (!r.ok) throw new Error(`meta ${tf} http ${r.status}`);
        return r.json();
      })
      .then((meta: TimeframeMeta) => {
        const store = new TimeframeStore(this.symbol, meta);
        this.tfStores.set(tf, store);
        this.tfMetaPromises.delete(tf);
        return store;
      });
    this.tfMetaPromises.set(tf, p);
    return p;
  }
}
